import React from 'react';
import { FormSection } from '../../common/FormSection';
import { FormCheckboxGroup } from '../../common/FormCheckboxGroup';
import { BehaviourSupportPlanData } from 'src/models/form-types';

interface BehavioursSectionProps {
  formData: BehaviourSupportPlanData;
  onTextChange: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onCheckboxChange: (name: string, checked: boolean, category: keyof BehaviourSupportPlanData) => void;
  className?: string;
}

// Define the behaviours options
const behaviourOptions = [
  {
    id: 'behaviour-calling',
    name: 'Continual calling out/interrupting',
    label: 'Continual calling out/interrupting',
  },
  {
    id: 'behaviour-unsafe',
    name: 'Unsafe behaviour or play',
    label: 'Unsafe behaviour or play',
  },
  {
    id: 'behaviour-social',
    name: 'Social skills and peer relationships',
    label: 'Social skills and peer relationships',
  },
  {
    id: 'behaviour-regulation',
    name: 'Emotional self-regulation and coping strategies',
    label: 'Emotional self-regulation and coping strategies',
  },
  {
    id: 'behaviour-participation',
    name: 'Participation in learning or school activities',
    label: 'Participation in learning or school activities',
  },
  {
    id: 'behaviour-escalation',
    name: 'Preventing escalation and crisis situations',
    label: 'Preventing escalation and crisis situations',
  },
  {
    id: 'behaviour-academic',
    name: 'Academic engagement and focus',
    label: 'Academic engagement and focus',
  },
];

// Define the behaviour context options
const behaviourContextOptions = [
  {
    id: 'context-difficult-tasks',
    name: 'Difficult/undesirable tasks',
    label: 'Difficult/undesirable tasks',
  },
  {
    id: 'context-routine-changes',
    name: 'Changes in routine or transitions',
    label: 'Changes in routine or transitions',
  },
  {
    id: 'context-social-demands',
    name: 'Social demands or group work',
    label: 'Social demands or group work',
  },
  {
    id: 'context-unfamiliar-adults',
    name: 'Unfamiliar adults present',
    label: 'Unfamiliar adults present',
  },
  {
    id: 'context-performance-anxiety',
    name: 'Performance anxiety or fear of failure',
    label: 'Performance anxiety or fear of failure',
  },
  {
    id: 'context-explicit-instruction',
    name: 'During explicit instruction',
    label: 'During explicit instruction',
  },
  {
    id: 'context-emotional-triggers',
    name: 'Emotional triggers',
    label: 'Emotional triggers',
  },
  {
    id: 'context-sensory-overload',
    name: 'Sensory overload',
    label: 'Sensory overload',
  },
  {
    id: 'context-sensory-understimulation',
    name: 'Sensory under-stimulation',
    label: 'Sensory under-stimulation',
  },
];

// Define the behaviour function options
const behaviourFunctionOptions = [
  {
    id: 'function-seeking-help',
    name: 'Seeking help/attention',
    label: 'Seeking help/attention',
  },
  {
    id: 'function-avoidance',
    name: 'Avoidance or escape',
    label: 'Avoidance or escape',
  },
  {
    id: 'function-seeking-access',
    name: 'Seeking access to object/activity/privilege',
    label: 'Seeking access to object/activity/privilege',
  },
  {
    id: 'function-sensory-stimulation',
    name: 'Seeking sensory stimulation',
    label: 'Seeking sensory stimulation',
  },
  {
    id: 'function-power-control',
    name: 'Power or control',
    label: 'Power or control',
  },
  {
    id: 'function-frustration',
    name: 'Frustration/anxiety/distress',
    label: 'Frustration/anxiety/distress',
  },
  {
    id: 'function-unmet-needs',
    name: 'Unmet basic needs e.g. hunger, lack of sleep',
    label: 'Unmet basic needs e.g. hunger, lack of sleep',
  },
];

export const BehavioursSection: React.FC<BehavioursSectionProps> = ({ 
  formData, 
  onTextChange, 
  onCheckboxChange, 
  className
}) => {
  const handleBehavioursCheckboxChange = (name: string, checked: boolean) => {
    onCheckboxChange(name, checked, 'behaviours');
  };

  const handleContextCheckboxChange = (name: string, checked: boolean) => {
    onCheckboxChange(name, checked, 'behaviourContext');
  };

  const handleFunctionCheckboxChange = (name: string, checked: boolean) => {
    onCheckboxChange(name, checked, 'behaviourFunction');
  };

  return (
    <>
      <FormSection title="What behaviours are you interested in addressing for the student? *" className={className}>
        <FormCheckboxGroup
          options={behaviourOptions}
          selectedValues={formData.behaviours}
          onChange={handleBehavioursCheckboxChange}
          columns={2}
        />
        
        <div className="other-input-container">
          <label className="other-input-label" htmlFor="otherBehaviours">Other:</label>
          <input
            type="text"
            id="otherBehaviours"
            name="otherBehaviours"
            value={formData.otherBehaviours}
            onChange={onTextChange}
            className="other-input"
          />
        </div>
      </FormSection>
      
      <FormSection title="What is the context of the behaviour? *">
        <FormCheckboxGroup
          options={behaviourContextOptions}
          selectedValues={formData.behaviourContext}
          onChange={handleContextCheckboxChange}
          columns={2}
        />
        
        <div className="other-input-container">
          <label className="other-input-label" htmlFor="otherBehaviourContext">Other:</label>
          <input
            type="text"
            id="otherBehaviourContext"
            name="otherBehaviourContext"
            value={formData.otherBehaviourContext}
            onChange={onTextChange}
            className="other-input"
          />
        </div>
      </FormSection>
      
      <FormSection title="What do you believe to be the function of the behaviour? *">
        <FormCheckboxGroup
          options={behaviourFunctionOptions}
          selectedValues={formData.behaviourFunction}
          onChange={handleFunctionCheckboxChange}
          columns={2}
        />
        
        <div className="other-input-container">
          <label className="other-input-label" htmlFor="otherBehaviourFunction">Other:</label>
          <input
            type="text"
            id="otherBehaviourFunction"
            name="otherBehaviourFunction"
            value={formData.otherBehaviourFunction}
            onChange={onTextChange}
            className="other-input"
          />
        </div>
      </FormSection>
    </>
  );
};
